import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';

import { IcCallIcon, IcMuteIcon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { PopupStore } from '@gem/popups';
import { Button, Divider, IconButton, IconView, MenuItem, Switch } from '@gem/ui-kit';

import { AvatarTypes } from 'APP/components/Avatar/Avatar';
import { UserAvatar } from 'APP/components/Avatar/UserAvatar/UserAvatar';
import { ProfileNameBadge } from 'APP/components/ProfileNameBadge/ProfileNameBadge';
import { ProfileEmail } from 'APP/main/Profile/components/ProfileEmail/ProfileEmail';
import { ProfilePosition } from 'APP/main/Profile/components/ProfilePosition/ProfilePosition';

import { PhoneNumber } from '../components/PhoneNumber/PhoneNumber';
import { ProfileNickname } from '../components/ProfileNickname/ProfileNickname';
import { IUserProfileInitiatorSource, useUserProfilePresenter } from './UserProfile.presenter';
import styles from './UserProfile.styles.m.css';
import { UserProfileJoinCallButton } from './UserProfileJoinCallButton/UserProfileJoinCallButton';

interface IUserProfileProps {
  className: string;
  metaBlockClassName: string;
  userId: string;
  groupId: string;
  initiatorSource?: IUserProfileInitiatorSource;
  hasActions?: boolean;
  actions: any;
  popupInstance?: PopupStore;
  onStartCall(): void;
}

export const UserProfile: FC<IUserProfileProps> = observer(
  ({
    className,
    metaBlockClassName,
    userId,
    groupId,
    initiatorSource,
    hasActions = true,
    actions,
    popupInstance,
    onStartCall,
  }) => {
    const { t } = useTranslation();
    const presenter = useUserProfilePresenter({
      userId,
      groupId,
      initiatorSource,
      actions,
      popupInstance,
      onStartCall,
    });

    //Todo modify the output of the remote user presenter.user === null
    if (!presenter.user) {
      return null;
    }

    return (
      <div className={classNames(styles.container, className)}>
        <div className={styles.header}>
          <UserAvatar
            className={styles.avatar}
            id={presenter.user.id}
            type={AvatarTypes.Round_20}
            withFullScreen={true}
          />
          <div className={styles.info}>
            <h3 className={styles.title}>
              {presenter.user?.displayName}
              <ProfileNameBadge
                verified={presenter.verified}
                className={styles.badge}
                inlineBadge
                source={presenter.userNameBadge?.source}
                animated={presenter.userNameBadge?.animated}
                displayUILayer={1}
              />
            </h3>
            <p className={styles.subtitle}>{presenter.userOnlineStatusText}</p>
          </div>
        </div>

        {hasActions && (
          <>
            <div className={styles.actions}>
              <Button
                theme="primary"
                onClick={presenter.startChat}
              >
                {t('profile_user_send_message')}
              </Button>
              {presenter.canJoinToCall && (
                <UserProfileJoinCallButton onClick={presenter.joinToBackgroundCall} />
              )}
              {presenter.canStartCall && (
                <>
                  <IconButton
                    theme="6"
                    Icon={IcCallIcon}
                    onClick={presenter.startAudioCall}
                    className={styles.actionButton}
                    iconClassName={styles.actionButtonIcon}
                    title={t('call_profile_audio_call')}
                  />
                </>
              )}
              {presenter.hasActiveCall && (
                <UserProfileJoinCallButton onClick={presenter.goToChat} />
              )}
            </div>
            <Divider theme="style1" />
          </>
        )}

        {presenter.user?.position && (
          <ProfilePosition
            className={classNames(styles.contact, metaBlockClassName)}
            position={presenter.user.position}
          />
        )}
        {presenter.user && (
          <ProfileNickname
            className={classNames(styles.contact, metaBlockClassName)}
            user={presenter.user}
          />
        )}
        {presenter.user?.displayPhone && (
          <PhoneNumber
            className={classNames(styles.contact, metaBlockClassName)}
            phoneNumber={presenter.user.displayPhone}
          />
        )}
        {presenter.user?.email && (
          <ProfileEmail
            className={classNames(styles.contact, metaBlockClassName)}
            email={presenter.user.email}
          />
        )}

        {hasActions &&
          presenter.hasConversationWithUser &&
          presenter.group?.notificationsAvailable && (
            <div className={styles.notifications}>
              <Divider theme="style2" />
              <MenuItem
                className={styles.listItem}
                title={t('common_notifications')}
                icon={
                  <IconView
                    Icon={IcMuteIcon}
                    className={styles.listIcon}
                  />
                }
                onClick={presenter.toggleNotifications}
              >
                <Switch
                  className={styles.switch}
                  isChecked={presenter.isNotificationsEnabled}
                  onChange={presenter.toggleNotifications}
                />
              </MenuItem>
            </div>
          )}

        {presenter.childPagesList.length > 0 && <Divider theme="style1" />}
        {presenter.childPagesList.map(({ key, title, Icon, onClick, count }) => (
          <MenuItem
            className={styles.listItem}
            key={key}
            title={title}
            count={count}
            icon={
              <IconView
                Icon={Icon}
                className={styles.listIcon}
              />
            }
            onClick={onClick}
          />
        ))}

        <Divider theme="style1" />
        {presenter.actionItems.map(({ key, title, Icon, onClick, value }) => (
          <MenuItem
            value={value}
            className={styles.listItem}
            key={key}
            title={title}
            icon={
              <IconView
                Icon={Icon}
                className={styles.listIcon}
              />
            }
            onClick={onClick}
          />
        ))}
      </div>
    );
  }
);
